<script setup lang="ts">
/**
 * DeliveryShareCard — สัดส่วนยอดขายจากช่องทางส่งอาหาร (home, bead
 * lucky-war-etcn.5).
 *
 * Compact home card fed by /sales/delivery-trend (chain or brand lens).
 * Advisor-consensus rules baked in:
 *  - Denominator labeled explicitly: the share is delivery-app revenue over
 *    NET SALES (฿ over ฿, window aggregate) — not a bill count share.
 *  - Horizontal uPlot lollipop chart of the delivery-brand mix — ไลน์แมน/
 *    แกร็บ/โรบินฮู้ด/ช้อปปี้ฟู้ด sorted desc, plus a ไม่ทราบช่องทาง row
 *    that only renders when delivery revenue exceeds classified codes.
 *  - Analysis line is RULE-BASED, phrased as a share difference in percentage
 *    points, normalized per day: mean of per-day shares on
 *    weekdays vs weekend days. Suppressed when <14 days of data (2 full
 *    weeks) or channel classification coverage <90%.
 *  - Margin caveat: commissions differ per platform — this is revenue
 *    share, NOT net profit.
 *  - Base note: จากข้อมูล N วัน · จำแนกช่องทางได้ X%.
 *
 * Calm-state rules: no fake donut slices, thin data hides the analysis
 * line rather than showing a noisy diff, zero delivery revenue shows an
 * honest 0.0% + no chart.
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { fetchDeliveryTrend } from '@/api';
import type { DeliveryTrendResponse } from '@/api';
import { lensFromQuery, scopeLabel } from '@/lib/brand';
import { cssVar } from '@/lib/display';

const props = defineProps<{ brand?: string }>();
const route = useRoute();
const lens = computed(() => lensFromQuery(route.query.brand));

const data = ref<DeliveryTrendResponse | null>(null);
const error = ref(false);
const loading = ref(false);

async function load() {
  loading.value = true;
  error.value = false;
  try {
    // 45 dates are enough to recover a complete latest calendar month even
    // when the newest POS upload trails today by a few days.
    data.value = await fetchDeliveryTrend({ brand: props.brand, days: 45 });
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
}
onMounted(load);
watch(() => props.brand, load);

// ── Aggregates over the data window ────────────────────────────────────────
const rawSeries = computed(() => data.value?.series ?? []);
const latestMonth = computed(() => rawSeries.value.at(-1)?.date.slice(0, 7) ?? '');
const series = computed(() => latestMonth.value
  ? rawSeries.value.filter((day) => day.date.startsWith(latestMonth.value))
  : []);
const daysWithData = computed(() => series.value.length);

const windowLabel = computed(() => {
  const month = latestMonth.value;
  if (!month) return 'ช่วงข้อมูลนี้';
  const currentParts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', timeZone: 'Asia/Bangkok',
  }).formatToParts(new Date());
  const currentICTMonth = `${currentParts.find((part) => part.type === 'year')?.value}-${currentParts.find((part) => part.type === 'month')?.value}`;
  if (month === currentICTMonth) return 'เดือนนี้';
  return new Intl.DateTimeFormat('th-TH', { month: 'long', year: 'numeric', timeZone: 'Asia/Bangkok' })
    .format(new Date(`${month}-01T00:00:00+07:00`));
});

const totalNet = computed(() => series.value.reduce((s, d) => s + d.net_total, 0));
const totalDelivery = computed(() => series.value.reduce((s, d) => s + d.delivery_total, 0));

/** Window share = Σ delivery ฿ / Σ net ฿ (sales share, not bills). */
const sharePct = computed(() => (totalNet.value > 0 ? (totalDelivery.value / totalNet.value) * 100 : null));

// ── Brand mix (horizontal lollipop chart) ──────────────────────────────────
const APP_LABELS: Record<string, string> = {
  CHLM: 'ไลน์แมน',
  GRP: 'แกร็บ',
  RBH: 'โรบินฮู้ด',
  SPF: 'ช้อปปี้ฟู้ด',
};
interface MixSlice {
  key: string;
  label: string;
  baht: number;
  pct: number;   // share of delivery revenue
}

const mix = computed<MixSlice[]>(() => {
  if (totalDelivery.value <= 0) return [];
  const byCode: Record<string, number> = {};
  for (const d of series.value) {
    for (const [code, baht] of Object.entries(d.delivery_apps ?? {})) {
      byCode[code] = (byCode[code] ?? 0) + baht;
    }
  }
  let classified = 0;
  for (const code of Object.keys(APP_LABELS)) classified += byCode[code] ?? 0;
  const unknown = Math.max(0, totalDelivery.value - classified);

  const slices: MixSlice[] = [];
  for (const [code, label] of Object.entries(APP_LABELS)) {
    const baht = byCode[code] ?? 0;
    if (baht > 0) {
      slices.push({ key: code, label, baht, pct: (baht / totalDelivery.value) * 100 });
    }
  }
  if (unknown > 0) {
    slices.push({ key: 'UNKNOWN', label: 'ไม่ทราบช่องทาง', baht: unknown, pct: (unknown / totalDelivery.value) * 100 });
  }
  slices.sort((a, b) => b.baht - a.baht);
  return slices;
});

/** Share of delivery revenue attributable to a known platform (0–1). */
const classifiedCoverage = computed(() => {
  if (totalDelivery.value <= 0) return null;
  const unknown = mix.value.find((s) => s.key === 'UNKNOWN');
  return 1 - (unknown?.pct ?? 0) / 100;
});

// ── Weekday/weekend share difference (rule-based) ─────────────────────────
// Mean of PER-DAY shares in each group (per-day normalized — a heavy Sunday
// never outweighs a light Tuesday by construction). Suppressed under 14 days
// (<2 full weeks) or classification coverage <90%.
const weekdayWeekendLine = computed<string | null>(() => {
  if (daysWithData.value < 14) return null;
  if (classifiedCoverage.value != null && classifiedCoverage.value < 0.9) return null;
  let wdSum = 0, wdN = 0, weSum = 0, weN = 0;
  for (const d of series.value) {
    if (d.net_total <= 0) continue;
    const dow = new Date(`${d.date}T00:00:00Z`).getUTCDay(); // 0=Sun … 6=Sat
    const isWeekend = dow === 0 || dow === 6;
    if (isWeekend) { weSum += d.delivery_app_pct; weN++; }
    else { wdSum += d.delivery_app_pct; wdN++; }
  }
  if (wdN === 0 || weN === 0) return null;
  const wdAvg = wdSum / wdN;
  const weAvg = weSum / weN;
  const diffPP = (wdAvg - weAvg) * 100;
  if (Math.abs(diffPP) < 0.1) return null; // flat — no line
  const pts = Math.abs(diffPP).toFixed(1);
  return diffPP > 0
    ? `สัดส่วนเดลิเวอรี่วันธรรมดาสูงกว่าวันหยุด ${pts} จุดเปอร์เซ็นต์`
    : `สัดส่วนเดลิเวอรี่วันหยุดสูงกว่าวันธรรมดา ${pts} จุดเปอร์เซ็นต์`;
});

// uPlot provides the numeric percentage axis, category axis, sizing, and
// canvas. A draw hook paints each horizontal stem + endpoint because uPlot's
// built-in line paths are optimized for vertical time-series values.
const plotHost = ref<HTMLElement | null>(null);
let plot: uPlot | null = null;
let resizeObserver: ResizeObserver | null = null;

function plotHeight(rows: number): number {
  return Math.max(180, rows * 38 + 70);
}

function lollipopPlugin(rows: MixSlice[], trackMax: number): uPlot.Plugin {
  return {
    hooks: {
      draw: [(u) => {
        const dpr = window.devicePixelRatio || 1;
        const ctx = u.ctx;
        const stem = cssVar('--sales');
        ctx.save();
        ctx.lineCap = 'round';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const y = u.valToPos(rows.length - 1 - i, 'y', true);
          const x0 = u.valToPos(0, 'x', true);
          const x1 = u.valToPos(row.pct, 'x', true);
          const trackEnd = u.valToPos(trackMax, 'x', true);
          ctx.strokeStyle = cssVar('--surface-track');
          ctx.lineWidth = 14 * dpr;
          ctx.beginPath();
          ctx.moveTo(x0, y);
          ctx.lineTo(trackEnd, y);
          ctx.stroke();
          ctx.strokeStyle = stem;
          ctx.beginPath();
          ctx.moveTo(x0, y);
          ctx.lineTo(x1, y);
          ctx.stroke();
          const labelX = x1 + 12 * dpr;
          ctx.fillStyle = cssVar('--text');
          ctx.font = `700 ${11 * dpr}px Sarabun, sans-serif`;
          const pctLabel = `${row.pct.toFixed(1)}%`;
          ctx.fillText(pctLabel, labelX, y);
          const pctWidth = ctx.measureText(pctLabel).width;
          ctx.fillStyle = cssVar('--text-muted');
          ctx.font = `400 ${11 * dpr}px Sarabun, sans-serif`;
          ctx.fillText(` · ${fmtCompactBaht(row.baht)}`, labelX + pctWidth, y);
        }
        ctx.restore();
      }],
    },
  };
}

function buildPlot(rows: MixSlice[]) {
  if (!plotHost.value || rows.length === 0) return;
  plot?.destroy();
  const maxPct = Math.max(...rows.map((row) => row.pct));
  // Keep roughly one quarter of the scale free after the longest bar for its
  // “36.4% · ฿3.5M” label, then round the visible maximum to a clean 10%.
  const trackMax = Math.max(10, Math.ceil((maxPct * 1.35) / 10) * 10);
  const xSplits = Array.from({ length: 6 }, (_, index) => (trackMax / 5) * index);
  const height = plotHeight(rows.length);
  const rowSplits = rows.map((_, i) => i);
  const options: uPlot.Options = {
    width: plotHost.value.clientWidth || 600,
    height,
    cursor: { show: false, drag: { x: false, y: false, setScale: false } },
    legend: { show: false },
    scales: {
      x: { time: false, auto: false, range: [0, trackMax] },
      y: { auto: false, range: [-0.5, rows.length - 0.5] },
    },
    series: [
      { label: 'สัดส่วน' },
      { label: 'ช่องทาง', stroke: 'transparent', width: 0, points: { show: false } },
    ],
    axes: [
      {
        label: 'สัดส่วนยอดขายเดลิเวอรี่ (%)',
        labelFont: '600 11px Sarabun, sans-serif',
        labelSize: 28,
        size: 44,
        stroke: cssVar('--text-muted'),
        grid: { stroke: cssVar('--line-light'), width: 1 },
        ticks: { stroke: cssVar('--line') },
        font: '11px Sarabun, sans-serif',
        splits: () => xSplits,
        values: (_u, vals) => vals.map((value) => `${Math.round(value)}%`),
      } as uPlot.Axis,
      {
        size: 94,
        stroke: cssVar('--text-dim'),
        grid: { show: false },
        ticks: { show: false },
        font: '12px Sarabun, sans-serif',
        splits: () => rowSplits,
        values: (_u, vals) => vals.map((value) => {
          const index = rows.length - 1 - Math.round(value);
          return rows[index]?.label ?? '';
        }),
      } as uPlot.Axis,
    ],
    plugins: [lollipopPlugin(rows, trackMax)],
  };
  const data = [[0, trackMax], [0, rows.length - 1]] as uPlot.AlignedData;
  plot = new uPlot(options, data, plotHost.value);
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver ??= new ResizeObserver((entries) => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0);
      if (width > 0 && plot) plot.setSize({ width, height });
    });
    resizeObserver.disconnect();
    resizeObserver.observe(plotHost.value);
  }
}

watch(mix, async (rows) => {
  await nextTick();
  if (rows.length === 0) {
    plot?.destroy();
    plot = null;
    return;
  }
  buildPlot(rows);
});

onUnmounted(() => {
  plot?.destroy();
  plot = null;
  resizeObserver?.disconnect();
  resizeObserver = null;
});

function fmtBaht(n: number): string {
  return `฿${Math.round(n).toLocaleString('th-TH')}`;
}

function fmtCompactBaht(n: number): string {
  if (n >= 100_000) return `฿${(Math.round(n / 100_000) / 10).toFixed(1)}M`;
  if (n > 0) return '<฿0.1M';
  return fmtBaht(n);
}
</script>

<template>
  <section class="dsc" :aria-label="`สัดส่วนยอดขายจากช่องทางส่งอาหาร · ${scopeLabel(lens)}`">
    <h2 class="card-head">ช่องทางส่งอาหาร · {{ scopeLabel(lens) }}</h2>

    <p v-if="loading" class="dsc__state">กำลังโหลดข้อมูล...</p>
    <p v-else-if="error" class="dsc__state">ไม่สามารถโหลดข้อมูลได้</p>
    <template v-else-if="data">
      <p v-if="series.length === 0 || totalNet === 0" class="dsc__state">ยังไม่มีข้อมูลยอดขาย</p>
      <template v-else>
        <!-- Hero: window share, denominator labeled -->
        <div class="dsc__hero">
          <span class="dsc__hero-value">{{ fmtCompactBaht(totalDelivery) }}</span>
          <span class="dsc__hero-label">= {{ sharePct != null ? `${sharePct.toFixed(1)}%` : '—' }} ของยอดขาย{{ windowLabel }}</span>
        </div>

        <!-- uPlot horizontal lollipop chart; list is its screen-reader equivalent. -->
        <div v-if="mix.length > 0" class="dsc__chart-wrap">
          <div ref="plotHost" class="dsc__plot" role="img" aria-label="กราฟสัดส่วนยอดขายเดลิเวอรี่แยกตามช่องทาง"></div>
          <ul class="sr-only" aria-label="ข้อมูลสัดส่วนยอดขายเดลิเวอรี่แยกตามช่องทาง">
            <li v-for="s in mix" :key="s.key">{{ s.label }} {{ s.pct.toFixed(1) }}% {{ fmtCompactBaht(s.baht) }}</li>
          </ul>
        </div>
        <p v-else class="dsc__state">ไม่มีรายได้จากช่องทางส่งอาหารในช่วงข้อมูล</p>

        <!-- Rule-based analysis line (suppressed on thin data) -->
        <p v-if="weekdayWeekendLine" class="dsc__analysis">{{ weekdayWeekendLine }}</p>

        <!-- Margin caveat (always on when data exists) -->
        <p class="dsc__caveat">ค่าคอมมิชชันแต่ละแพลตฟอร์มต่างกัน — ตัวเลขนี้เป็นสัดส่วนยอดขาย ยังไม่ใช่กำไรสุทธิ</p>

        <!-- Base note -->
        <p class="dsc__base">
          จากข้อมูล {{ daysWithData }} วัน<template v-if="classifiedCoverage != null"> · จำแนกช่องทางได้ {{ (classifiedCoverage * 100).toFixed(0) }}%</template>
        </p>
      </template>
    </template>
  </section>
</template>

<style scoped>
.dsc {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  padding: 0.85rem 0.9rem;
  margin-bottom: 1rem; /* home-card rhythm — was missing (flush against the section below) */
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  box-shadow: none;
}

.dsc__state {
  margin: 0;
  color: var(--text-muted);
  text-align: center;
  padding: 0.5rem 0;
  font-size: 0.8rem;
}

.dsc__hero {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.dsc__hero-value {
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.dsc__hero-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text);
}

/* ── uPlot channel-share lollipop ───────────────────────────────────────── */
.dsc__chart-wrap {
  min-width: 0;
}

.dsc__plot {
  width: 100%;
  min-height: 180px;
  overflow: hidden;
  background: transparent;
  border: 0;
  border-radius: var(--radius-badge);
}

.dsc__plot :deep(.u-title),
.dsc__plot :deep(.u-legend) {
  display: none;
}

/* ── Analysis + caveats ─────────────────────────────────────────────────── */
.dsc__analysis {
  margin: 0;
  padding: 0.4rem 0.55rem;
  background: var(--surface-2);
  border-radius: var(--radius-badge);
  font-size: 0.72rem;
  color: var(--text-dim);
}

.dsc__caveat {
  margin: 0;
  font-size: 0.62rem;
  color: var(--text-muted);
}

.dsc__base {
  margin: 0;
  font-size: 0.62rem;
  color: var(--text-faint);
}
</style>
